import { useCallback, useEffect, useMemo, useState } from "react";
import { MondayBoardSelector } from "frontend/canvas-designer-new/monday-import-items/monday-board-selector/monday-board-selector";
import { useMondayImportItems } from "frontend/canvas-designer-new/monday-import-items/monday-import.hook";
import { useBoardIntegrations } from "frontend/hooks/use-board-integrations";
import { useBoardValue } from "frontend/state/board-state";
import { MondayIntegrationType } from "shared/integrations/integration";
import { LiveIntegrationFilter } from "frontend/canvas-designer-new/elements-toolbar/widgets/live-integration/utils";
import ConfirmButton from "../../components/confirm-button";
import SyncedWidgetHeader from "../../components/synced-widget-header";
import styles from "./synced-card-stack.module.css";
import SyncedCardStackFilters from "./filters/synced-card-stack-filters";
import { CardStackSkeleton } from "./card-stack-skeleton";
import { ResourceType } from "frontend/canvas-designer-new/monday-import-items/monday-import-items.types";
import PreviewIcon from "./preview-icon";

export default function SyncedCardStack({ onComplete }: { onComplete: (data: unknown) => void }) {
  const [{ board }] = useBoardValue();
  const { mondayIntegration, addBoardIntegration } = useBoardIntegrations(board?.documentId);

  const {
    workspaces,
    workspacesLoading,
    workspacesInitialLoading,
    loadMoreWorkspaces,
    selectedWorkspace,
    setSelectedWorkspace,
    boards,
    boardsLoading,
    boardsInitialLoading,
    selectedBoard,
    setSelectedBoard,
    loadMoreBoards,
  } = useMondayImportItems({ withColumns: true });

  const [isCreating, setIsCreating] = useState(false);
  const [filters, setFilters] = useState<LiveIntegrationFilter[]>([]);

  // Get columns from the selected board (fetched with withColumns: true)
  const columns = useMemo(() => {
    const boardColumns = (
      selectedBoard as {
        columns?: Array<{ id: string; type: string; title: string; settings_str?: string }>;
      }
    )?.columns;
    if (!boardColumns) return [];
    return boardColumns.map((c) => ({
      id: c.id,
      type: c.type,
      title: c.title,
      settings: c.settings_str ? JSON.parse(c.settings_str) : {},
    }));
  }, [selectedBoard]);

  useEffect(() => {
    setFilters([]);
  }, [selectedBoard?.id, selectedWorkspace?.id]);

  const handleCreateCardStack = useCallback(async () => {
    if (!selectedBoard || !mondayIntegration || isCreating) {
      return;
    }

    setIsCreating(true);

    try {
      const configuration = {
        boardId: selectedBoard.id,
        type: MondayIntegrationType.live,
        columns,
        filters,
      };

      const integrationId = await addBoardIntegration(mondayIntegration.id, configuration);

      const cardStackData = {
        integrationId,
        title: selectedBoard.name,
        filters,
      };

      onComplete(cardStackData);
    } catch (error) {
      console.error("Error creating card stack:", error);
      setIsCreating(false);
    }
  }, [selectedBoard, mondayIntegration, isCreating, columns, filters, addBoardIntegration, onComplete]);

  const handleBoardSelect = useCallback(
    (pickedBoard: ResourceType) => {
      const fullBoard = boards.find((b) => b.id === pickedBoard.id);
      setSelectedBoard(fullBoard ?? null);
    },
    [boards, setSelectedBoard]
  );

  const isCreateDisabled = !selectedBoard || !selectedWorkspace || !mondayIntegration || isCreating;

  if (workspacesInitialLoading) {
    return <CardStackSkeleton />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.previewImageContainer}>
          <PreviewIcon />
        </div>
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.selectorsRow}>
          <SyncedWidgetHeader title="Import data for a live view" subtitle="Apply rules to control data presentation" />
        </div>
        <div className={styles.selectorsRow}>
          <MondayBoardSelector
            workspaces={workspaces}
            selectedWorkspace={selectedWorkspace}
            setSelectedWorkspace={setSelectedWorkspace}
            workspacesLoading={workspacesLoading}
            workspacesInitialLoading={workspacesInitialLoading}
            loadMoreWorkspaces={loadMoreWorkspaces}
            boards={boards}
            selectedBoard={selectedBoard}
            setSelectedBoard={handleBoardSelect}
            boardsLoading={boardsLoading}
            boardsInitialLoading={boardsInitialLoading}
            loadMoreBoards={loadMoreBoards}
            workspacePickerTitle="Select workspace"
            boardPickerTitle="Select board"
            pickerWidth="158px"
            pickerStyle={{
              color: "#1E1E1E",
              fontSize: "10px",
              fontWeight: 400,
              marginBottom: "4px",
            }}
            useFloatingDropdown={true}
          />
        </div>

        <SyncedCardStackFilters columns={columns} filters={filters} onFiltersChange={setFilters} />
      </div>

      <div className={styles.createButtonContainer}>
        <ConfirmButton
          title={isCreating ? "Creating..." : "Create"}
          disabled={isCreateDisabled}
          onClick={handleCreateCardStack}
          trackingEventName="cim_card_stack_created"
        />
      </div>
    </div>
  );
}
